import { useRef, useState } from 'react'
import { requestCommerceManagement } from '../management-api.js'
import { minorToMajor, originalRefundPayment, pendingRefundOperation, beginRefundOperation, settleRefundOperation, refundableAmount, refundPayload } from '../management-model.js'

export default function PaymentActions({ aggregate, lang = 'zh', operationKeys, onBusyChange, onSuccess }) {
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('customer_request')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const lock = useRef(false)
  const localKeys = useRef(new Map())
  const keys = operationKeys || localKeys.current
  const order = aggregate?.order || aggregate
  const pending = pendingRefundOperation(keys, aggregate)
  const [confirmation, setConfirmation] = useState(() => pending?.state !== 'succeeded' ? pending?.body || null : null)
  const retryLocked = pending?.state === 'unknown' || pending?.state === 'pending'
  const t = (zh, en) => lang === 'zh' ? zh : en
  const available = refundableAmount(aggregate)
  let balance = '—'
  try { balance = minorToMajor(available, order?.currency) } catch { /* Fail closed for unknown currency. */ }
  const eligible = originalRefundPayment(aggregate) && order?.id && available > 0 && balance !== '—'
  const disabled = busy || submitted || pending?.state === 'succeeded' || (!retryLocked && !eligible)
  const button = 'rounded border border-slate-300 px-3 py-2 text-sm disabled:opacity-50 dark:border-slate-600'

  function review(event) {
    event.preventDefault()
    if (disabled || lock.current) return
    setError('')
    try { setConfirmation(refundPayload(aggregate, amount, reason)) } catch (cause) { setError(cause.message) }
  }

  async function submit() {
    if (!confirmation || disabled || lock.current) return
    lock.current = true; setBusy(true); onBusyChange?.(true); setError('')
    let operation
    try {
      operation = beginRefundOperation(keys, aggregate, confirmation)
      const path = `orders/${encodeURIComponent(order.id)}/refunds`
      await requestCommerceManagement(path, { method: 'POST', body: confirmation, idempotencyKey: operation.key })
      settleRefundOperation(keys, aggregate, operation)
      setSubmitted(true); setConfirmation(null); setAmount('')
      // Retain this key until the authoritative refund ledger changes.
      await onSuccess?.(t('退款请求已提交，最终结果以支付渠道及订单退款记录为准。', 'Refund requested. Check the provider and order refund records for the final outcome.'))
    } catch (cause) {
      if (operation && operation.state !== 'succeeded') settleRefundOperation(keys, aggregate, operation, cause)
      setError(cause.message)
    }
    finally { lock.current = false; setBusy(false); onBusyChange?.(false) }
  }

  return <section className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-700" aria-label={t('订单退款', 'Order refund')}>
    <h3 className="text-sm font-bold">{t('订单退款', 'Order refund')}</h3>
    <p className="text-sm">{t('可退款余额（已扣除处理中退款）', 'Refundable balance (pending refunds excluded)')}: {balance} {order?.currency}</p>
    {!eligible && <p className="text-sm text-slate-500">{t('此订单暂无可退款余额，或缺少已成功付款及原支付渠道信息。', 'No refundable balance, or successful payment/original provider information is missing.')}</p>}
    {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
    {retryLocked && !busy && <p role="status">{t('退款结果尚不明确：金额和原因已锁定，只能重试原请求。', 'Refund outcome unknown: amount and reason are locked. Retry only the original request.')}</p>}
    {busy && <p role="status">{t('正在提交退款…', 'Submitting refund…')}</p>}
    <form onSubmit={review}>
      <fieldset disabled={Boolean(disabled || confirmation)} className="space-y-3 text-sm">
        <label className="block">{t('退款金额', 'Refund amount')} ({order?.currency || '—'})<input className="mt-1 block w-full rounded border border-slate-300 bg-transparent p-2 dark:border-slate-600" required inputMode="decimal" autoComplete="off" value={amount} placeholder={balance} onChange={(event) => setAmount(event.target.value)} /></label>
        <label className="block">{t('退款原因', 'Refund reason')}<select className="mt-1 block w-full rounded border border-slate-300 bg-transparent p-2 dark:border-slate-600" value={reason} onChange={(event) => setReason(event.target.value)}><option value="customer_request">{t('客户要求', 'Customer request')}</option><option value="duplicate">{t('重复付款', 'Duplicate')}</option><option value="fraud">{t('欺诈', 'Fraud')}</option></select></label>
        <button type="submit" className={button}>{t('审核退款', 'Review refund')}</button>
      </fieldset>
    </form>
    {confirmation && <div role="group" aria-label={t('确认退款', 'Confirm refund')} className="space-y-3 rounded border border-amber-400 p-3 text-sm">
      <p>{t('确认向原支付方式退款？', 'Refund to the original payment method?')} {order.number || order.id} · {minorToMajor(confirmation.amount, order.currency)} {order.currency} · {confirmation.provider}</p>
      <p>{t('提交后无法在此撤销。失败重试会使用同一请求标识，避免重复退款。', 'This cannot be undone here. Retrying uses the same request key to prevent duplicate refunds.')}</p>
      <div className="flex gap-2"><button type="button" className={button} disabled={Boolean(disabled)} onClick={() => void submit()}>{t('确认提交退款', 'Confirm refund')}</button><button type="button" className={button} disabled={busy || retryLocked} onClick={() => setConfirmation(null)}>{t('取消', 'Cancel')}</button></div>
    </div>}
  </section>
}
